import { useState } from "react";
import TextBundle from "aldoo-ra/CMS/text-bundle";
import Modal from "aldoo-ra/Modal";
import { sendInvites } from "./api";

export default function InviteUsersDialog({ orgId, onClose }) {
  const [inputValue, setInputValue] = useState("");
  const [emails, setEmails] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const texts = TextBundle({ id: "aldoo_ra_organizations" });

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function addFromInput() {
    const parts = inputValue
      .split(/[,;\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(isValidEmail);
    if (!parts.length) return;
    setEmails((prev) => [...new Set([...prev, ...parts])]);
    setInputValue("");
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addFromInput();
    }
  }

  function removeEmail(email) {
    setEmails((prev) => prev.filter((e) => e !== email));
  }

  async function handleSend() {
    if (!emails.length) return;
    setSending(true);
    setError(null);
    try {
      const res = await sendInvites(orgId, emails);
      if (res.error) {
        setError(res.error);
        return;
      }
      setResult(res);
      setEmails([]);
    } catch {
      setError(texts?.send_failed);
    } finally {
      setSending(false);
    }
  }

  if (!texts) return null;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      variant="centered"
      className="max-w-md"
      title={texts?.invite_people_title}
    >
      {result ? (
        <div className="space-y-4">
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="w-12 h-12 rounded-full bg-success/15 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-success"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            {result.sent?.length > 0 && (
              <div className="text-center">
                <p className="text-sm font-medium text-text">
                  {result.sent.length === 1
                    ? texts?.invitation_sent_single
                    : `${result.sent.length} ${texts?.invitations_sent_plural}`}
                </p>
                <p className="text-xs text-text_secondary mt-1">
                  {result.sent.join(", ")}
                </p>
              </div>
            )}
            {result.skipped?.length > 0 && (
              <p className="text-xs text-text_secondary text-center">
                {result.skipped.length}{" "}
                {result.skipped.length === 1 ? texts?.address : texts?.addresses}{" "}
                {texts?.skipped_suffix}
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-bordered text-sm font-medium text-text hover:bg-black/5 transition-colors"
            >
              {texts?.done}
            </button>
            <button
              onClick={() => {
                setResult(null);
                setInputValue("");
              }}
              className="flex-1 py-2.5 rounded-xl bg-primary hover:bg-primary_dk text-text_inv text-sm font-medium transition-colors"
            >
              {texts?.invite_more}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Input row */}
          <div className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={addFromInput}
              placeholder="name@example.com"
              className="flex-1 px-3 py-2.5 rounded-xl border border-bordered bg-canvas text-sm text-text placeholder-text_secondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <button
              onClick={addFromInput}
              className="px-4 py-2.5 rounded-xl bg-primary hover:bg-primary_dk text-text_inv text-sm font-medium transition-colors flex-shrink-0"
            >
              {texts?.add}
            </button>
          </div>

          {/* Email chips list */}
          {emails.length > 0 && (
            <div className="flex flex-wrap gap-2 p-3 rounded-xl border border-bordered bg-canvas min-h-[48px]">
              {emails.map((email) => (
                <span
                  key={email}
                  className="flex items-center gap-1 bg-primary/15 text-primary text-xs px-2.5 py-1 rounded-full"
                >
                  {email}
                  <button
                    onClick={() => removeEmail(email)}
                    className="hover:text-primary_dk ml-0.5"
                  >
                    <svg
                      className="w-3 h-3"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}

          {error && <p className="text-sm text-error">{error}</p>}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-bordered text-sm font-medium text-text hover:bg-black/5 transition-colors"
            >
              {texts?.cancel}
            </button>
            <button
              onClick={handleSend}
              disabled={sending || emails.length === 0}
              className="flex-1 py-2.5 rounded-xl bg-primary hover:bg-primary_dk disabled:opacity-50 disabled:cursor-not-allowed text-text_inv text-sm font-medium transition-colors"
            >
              {sending
                ? texts?.sending
                : emails.length > 1
                  ? texts?.send_invites
                  : texts?.send_invite}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}